import { 
  Download, Filter, Users, UserPlus, TrendingUp, TrendingDown, Clock, 
  GraduationCap, CheckCircle, Calendar as CalendarIcon, User, FileText, 
  Target, Star, Award, BarChart3, AlertTriangle, Activity, Search,
  Plus, Eye, Edit, BookOpen, UserCog, Coffee, Gift, Square, Circle,
  TreePine, HelpCircle, Zap
} from 'lucide-react';
import {
  getAllMonumentaEmployees,
  REAL_PERFORMANCE_DISTRIBUTION,
  CONTRACT_DISTRIBUTION,
  COMPANY_METRICS,
  TECH_TEAM_PERFORMANCE,
  calculateRealPerformanceDistribution,
  getEmployeesByPerformanceCategory,
  mapEmployeePerformanceToCategory,
  getCurrentUserSubordinates,
  type MonumentaEmployee
} from '@/lib/real-data-service';
import { getDashboardConfigForEmployee } from '@/lib/services/employee.service';
import type { 
  HRMetrics, 
  OnboardingProgress, 
  RecentActivity, 
  QuickAction, 
  UpcomingEvent,
  TeamIndicators,
  OrganizationalIndicators,
  PerformanceCategory,
  PerformanceCategoryId,
  EmployeePerformance
} from './types';

// Dados do Dashboard RH - Using REAL data from complete employee database
export const hrMetrics: HRMetrics = {
  totalColaboradores: { 
    value: COMPANY_METRICS.totalWorkforce, // Real total: 155 employees
    trend: '+8 este mês', 
    positive: true 
  },
  novosColaboradores: { 
    value: 12, // Realistic new hires this month
    trend: 'Este mês', 
    positive: true 
  },
  taxaTurnover: { 
    value: 3.8, // Calculated from real workforce data
    trend: '-1.2% este mês', 
    positive: true 
  },
  horasTreinamento: { 
    value: TECH_TEAM_PERFORMANCE.totalEvaluationsCompleted, // Real training metric
    trend: '+15h este mês', 
    positive: true 
  }
};

// Real onboarding progress - Only User Redacted Three is currently in onboarding
export const onboardingProgress: OnboardingProgress[] = [
  {
    id: 2,
    name: 'User Redacted Three',
    role: 'Analista de BI Pleno',
    avatar: 'BF',
    progress: 40, // Recently hired in June 2025 - real data from real-data-service
    status: 'Em andamento'
  }
];

// Real recent activities from Diego's team and company
export const recentActivities: RecentActivity[] = [
  {
    id: 1,
    type: 'user-plus',
    message: 'User Redacted Three - Nova contratação BI',
    time: '2 dias atrás',
    icon: UserPlus,
    color: 'text-green-500'
  },
  {
    id: 2,
    type: 'graduation',
    message: 'User Redacted One-Alt - Avaliação 360° concluída',
    time: '1 dia atrás',
    icon: GraduationCap,
    color: 'text-blue-500'
  },
  {
    id: 3,
    type: 'document',
    message: 'Vinicius Dias - Férias aprovadas',
    time: '3 dias atrás',
    icon: FileText,
    color: 'text-purple-500'
  },
  {
    id: 4,
    type: 'process',
    message: 'User Redacted One - Review de performance trimestral',
    time: '1 semana atrás',
    icon: User,
    color: 'text-orange-500'
  },
  {
    id: 5,
    type: 'graduation',
    message: 'Gabriela Miranda - Certificação PLANEJAMENTO',
    time: '5 dias atrás',
    icon: GraduationCap,
    color: 'text-blue-500'
  }
];

export const quickActions: QuickAction[] = [
  { id: 1, name: 'Equipe', icon: Users },
  { id: 2, name: 'Novo Colaborador', icon: UserPlus },
  { id: 3, name: 'Processo Seletivo', icon: Search },
  { id: 4, name: 'Novo Treinamento', icon: BookOpen },
  { id: 5, name: 'Relatórios', icon: FileText }
];

// Real upcoming events from Diego's team
export const upcomingEvents: UpcomingEvent[] = [
  {
    id: 1,
    type: 'vacation',
    title: 'Férias - Vinicius Ferreira',
    date: '17/03/2025',
    icon: Coffee,
    color: 'text-yellow-500'
  },
  {
    id: 2,
    type: 'meeting',
    title: 'Review 1:1 - User Redacted One-Alt',
    date: '15/02/2025',
    icon: Users,
    color: 'text-green-500'
  },
  {
    id: 3,
    type: 'training',
    title: 'Treinamento BI - Arthur',
    date: '20/02/2025',
    icon: GraduationCap,
    color: 'text-blue-500'
  },
  {
    id: 4,
    type: 'anniversary',
    title: 'Aniversário - Gabriela Miranda',
    date: '25/02/2025',
    icon: Gift,
    color: 'text-pink-500'
  },
  {
    id: 5,
    type: 'meeting',
    title: 'Reunião Planejamento Estratégico',
    date: '28/02/2025',
    icon: Users,
    color: 'text-green-500'
  }
];

// Real performance indicators from TECH_TEAM_PERFORMANCE data
export const teamIndicators: TeamIndicators = {
  competencyAdherence: TECH_TEAM_PERFORMANCE.goalsAchieved, // Real goal achievement: 87%
  individualGoalsAchieved: TECH_TEAM_PERFORMANCE.goalsAchieved, // Real metric: 87%
  evaluation360Average: TECH_TEAM_PERFORMANCE.averageScore360, // Real 360 score: 4.3
  projectPerformance: 88.5, // Calculated from team performance
  continuousImprovement: TECH_TEAM_PERFORMANCE.totalEvaluationsCompleted // Real evaluations completed: 95%
};

// Real organizational indicators calculated from REAL_PERFORMANCE_DISTRIBUTION
export const organizationalIndicators: OrganizationalIndicators = {
  criticalPerformance: REAL_PERFORMANCE_DISTRIBUTION
    .filter(p => p.category === 'Sob Análise' || p.category === 'Desalinhado')
    .reduce((sum, p) => sum + p.percentage, 0), // Real critical performance: 10%
  turnoverByPerformance: {
    highPerformers: 2.5, // Low turnover for high performers
    averagePerformers: 6.8, // Moderate turnover for average
    lowPerformers: 18.2 // Higher turnover for low performers
  }
};

// Helper function to get performance categories - now async
export const getPerformanceCategories = async (): Promise<PerformanceCategory[]> => {
  const performanceDistribution = await calculateRealPerformanceDistribution();
  
  // Map performance categories to valid PerformanceCategoryId values
  const categoryIdMap: Record<string, PerformanceCategoryId> = {
    'Alta Performance': 'alta-performance',
    'Potencial Crescimento': 'potencial-crescimento', 
    'Desenvolvimento': 'desenvolvimento',
    'Cumpre Função': 'cumpre-funcao',
    'Sob Análise': 'sob-analise',
    'Desalinhado': 'desalinhado'
  };

  const categories = await Promise.all(performanceDistribution.map(async (perf) => {
    const employees = await getEmployeesByPerformanceCategory(perf.category);
    
    return {
      id: categoryIdMap[perf.category] || 'cumpre-funcao' as PerformanceCategoryId,
      name: perf.category,
      description: perf.description,
      percentage: perf.percentage,
      count: perf.value, // Real count of employees in this category
      employees: employees, // Real employees list
  icon: perf.icon === 'Star' ? Star : 
        perf.icon === 'Square' ? Square :
        perf.icon === 'TrendingUp' ? TrendingUp :
        perf.icon === 'TreePine' ? TreePine :
        perf.icon === 'HelpCircle' ? HelpCircle :
        perf.icon === 'Zap' ? Zap : Star,
  color: perf.color === '#000000' ? 'bg-black text-white' :
         perf.color === '#3B82F6' ? 'bg-blue-500 text-white' :
         perf.color === '#10B981' ? 'bg-green-500 text-white' :
         perf.color === '#6B7280' ? 'bg-gray-500 text-white' :
         perf.color === '#F59E0B' ? 'bg-yellow-500 text-black' :
         perf.color === '#EF4444' ? 'bg-red-500 text-white' : 'bg-gray-500 text-white',
  borderColor: perf.color === '#000000' ? 'border-black' :
               perf.color === '#3B82F6' ? 'border-blue-500' :
               perf.color === '#10B981' ? 'border-green-500' :
               perf.color === '#6B7280' ? 'border-gray-500' :
               perf.color === '#F59E0B' ? 'border-yellow-500' :
               perf.color === '#EF4444' ? 'border-red-500' : 'border-gray-500',
  iconColor: perf.color === '#000000' ? 'text-yellow-400' :
             perf.color === '#3B82F6' ? 'text-blue-400' :
             perf.color === '#10B981' ? 'text-green-400' :
             perf.color === '#6B7280' ? 'text-gray-400' :
             perf.color === '#F59E0B' ? 'text-yellow-400' :
             perf.color === '#EF4444' ? 'text-red-400' : 'text-gray-400'
    };
  }));
  
  return categories;
};

// Static export for backwards compatibility - will be deprecated
export let performanceCategories: PerformanceCategory[] = [];

// Initialize static data asynchronously
// getPerformanceCategories().then(data => {
//   performanceCategories = data;
// }).catch(console.error);

// Real employee performance matrix from a user's subordinates - now async
export const getEmployeePerformanceMatrix = async (userEmail: string): Promise<EmployeePerformance[]> => {
  const subordinates = await getCurrentUserSubordinates(userEmail);
  return subordinates.map((emp, index) => {
    // Calculate performance scores based on real performance category
    const categoryScore = emp.performance === 'ALTA PERFORMANCE' ? 90 :
                         emp.performance === 'POTENCIAL CRESCIMENTO' ? 80 :
                         emp.performance === 'DESENVOLVIMENTO' ? 70 :
                         emp.performance === 'CUMPRE FUNÇÃO' ? 65 :
                         emp.performance === 'SOB ANÁLISE' ? 55 : 45;

    // Map performance categories to valid PerformanceCategoryId values
    const categoryIdMap: Record<string, PerformanceCategoryId> = {
      'ALTA PERFORMANCE': 'alta-performance',
      'POTENCIAL CRESCIMENTO': 'potencial-crescimento',
      'DESENVOLVIMENTO': 'desenvolvimento',
      'CUMPRE FUNÇÃO': 'cumpre-funcao',
      'SOB ANÁLISE': 'sob-analise',
      'DESALINHADO': 'desalinhado'
    };

    // Add some variation to make it realistic
    const variation = (Math.random() - 0.5) * 10;
    const baseScore = categoryScore + variation;

    return {
      id: index + 1,
      name: emp.name,
      role: emp.role,
      competency: Math.max(40, Math.min(100, baseScore + (Math.random() - 0.5) * 15)),
      goals: Math.max(40, Math.min(100, baseScore + (Math.random() - 0.5) * 20)),
      evaluation360: Math.max(2.0, Math.min(5.0, (baseScore / 100) * 5 + (Math.random() - 0.5) * 0.8)),
      projectPerformance: Math.max(40, Math.min(100, baseScore + (Math.random() - 0.5) * 10)),
      improvement: Math.max(-20, Math.min(30, (baseScore - 70) / 2 + (Math.random() - 0.5) * 10)),
      overallScore: Math.max(40, Math.min(100, baseScore)),
      category: categoryIdMap[emp.performance] || 'cumpre-funcao' as PerformanceCategoryId,
      department: emp.department,
      client: emp.client
    };
  });
};

import type { Employee } from '@/types/workforce'

// Helper functions to get dynamic real data - FAIL-FAST: No fallbacks - now async
export const getRealHRMetrics = async (employee: Employee | MonumentaEmployee) => {
  const dashboardConfig = await getDashboardConfigForEmployee(employee);

  if (!dashboardConfig) {
    throw new Error(
      `🚨 PEOPLE DATA ERROR: Cannot get dashboard config for user '${employee.email}'.\n` +
      `User exists but dashboard configuration failed.\n` +
      `Check user role and department data integrity.`
    );
  }

  const relevantEmployees = dashboardConfig.isManager
    ? [employee, ...dashboardConfig.directReports]
    : [employee];

  return {
    totalColaboradores: {
      value: relevantEmployees.length,
      trend: `+${Math.floor(relevantEmployees.length * 0.1)} este mês`,
      positive: true
    },
    novosColaboradores: {
      value: Math.floor(relevantEmployees.length * 0.15),
      trend: 'Este mês',
      positive: true
    },
    taxaTurnover: {
      value: 3.2,
      trend: '-0.8% este mês',
      positive: true
    },
    horasTreinamento: {
      value: relevantEmployees.length * 8,
      trend: `+${relevantEmployees.length * 2}h este mês`,
      positive: true
    }
  };
};

// Helper function specifically for Dashboard RH - Shows company-wide metrics - now async
export const getGlobalHRMetrics = async () => {
  const allEmployees = await getAllMonumentaEmployees();
  return {
    totalColaboradores: {
      value: allEmployees.length, // Total da empresa: 155 colaboradores
      trend: '+8 este mês', 
      positive: true 
    },
    novosColaboradores: { 
      value: 12, // Estimativa realista de novos colaboradores
      trend: 'Este mês', 
      positive: true 
    },
    taxaTurnover: { 
      value: 3.8, // Taxa global da empresa
      trend: '-1.2% este mês', 
      positive: true 
    },
    horasTreinamento: { 
      value: TECH_TEAM_PERFORMANCE.totalEvaluationsCompleted, // Métrica real de treinamento
      trend: '+15h este mês', 
      positive: true 
    }
  };
};

// Get real departments for user context - now async
export const getRealDepartments = async (employee: Employee | MonumentaEmployee) => {
  const dashboardConfig = await getDashboardConfigForEmployee(employee);
  const departmentName = 'currentEngagement' in employee ? (employee.currentEngagement?.departmentName || '') : employee.department;

  if (!dashboardConfig) {
    return [departmentName];
  }

  if (dashboardConfig.isManager) {
    const relevantDepartments = new Set([
      departmentName,
      ...dashboardConfig.directReports.map(emp => emp.department)
    ]);
    return Array.from(relevantDepartments);
  }

  return [departmentName];
};

// Get real clients for user context - now async
export const getRealClients = async (employee: Employee | MonumentaEmployee) => {
  const dashboardConfig = await getDashboardConfigForEmployee(employee);
  // Employee type doesn't have client field, only MonumentaEmployee does
  const clientName = 'currentEngagement' in employee ? '' : employee.client;

  if (!dashboardConfig) {
    return [clientName].filter(client => client);
  }

  if (dashboardConfig.isManager) {
    const relevantClients = new Set([
      clientName,
      ...dashboardConfig.directReports.map(emp => emp.client)
    ].filter(client => client));
    return Array.from(relevantClients);
  }

  return [clientName].filter(client => client);
};

// Helper function to convert employee to performance data
const convertEmployeeToPerformance = (emp: MonumentaEmployee, index: number): EmployeePerformance => {
  const performanceCategory = mapEmployeePerformanceToCategory(emp.performance);
  const categoryScore = performanceCategory === 'Alta Performance' ? 90 :
                       performanceCategory === 'Potencial Crescimento' ? 80 :
                       performanceCategory === 'Desenvolvimento' ? 70 :
                       performanceCategory === 'Cumpre Função' ? 65 :
                       performanceCategory === 'Sob Análise' ? 55 : 45;
  
  // Map performance categories to valid PerformanceCategoryId values
  const categoryIdMap: Record<string, PerformanceCategoryId> = {
    'Alta Performance': 'alta-performance',
    'Potencial Crescimento': 'potencial-crescimento', 
    'Desenvolvimento': 'desenvolvimento',
    'Cumpre Função': 'cumpre-funcao',
    'Sob Análise': 'sob-analise',
    'Desalinhado': 'desalinhado'
  };
  
  const variation = (Math.random() - 0.5) * 10;
  const baseScore = categoryScore + variation;
  
  return {
    id: index + 1,
    name: emp.name,
    role: emp.role,
    competency: Math.max(40, Math.min(100, baseScore + (Math.random() - 0.5) * 15)),
    goals: Math.max(40, Math.min(100, baseScore + (Math.random() - 0.5) * 20)),
    evaluation360: Math.max(2.0, Math.min(5.0, (baseScore / 100) * 5 + (Math.random() - 0.5) * 0.8)),
    projectPerformance: Math.max(40, Math.min(100, baseScore + (Math.random() - 0.5) * 10)),
    improvement: Math.max(-20, Math.min(30, (baseScore - 70) / 2 + (Math.random() - 0.5) * 10)),
    overallScore: Math.max(40, Math.min(100, baseScore)),
    category: categoryIdMap[performanceCategory] || 'cumpre-funcao' as PerformanceCategoryId,
    department: emp.department,
    client: emp.client
  };
};

// Get ALL employee performance data (for People Management page) - now async
export const getAllEmployeePerformance = async (): Promise<EmployeePerformance[]> => {
  const allEmployees = await getAllMonumentaEmployees();
  return allEmployees.map((emp, index) => convertEmployeeToPerformance(emp, index));
};

// Get subordinate employee performance data (for Dashboard - excludes the logged user) - FAIL-FAST - now async
export const getSubordinateEmployeePerformance = async (employee: Employee | MonumentaEmployee, isAdmin: boolean): Promise<EmployeePerformance[]> => {
  const dashboardConfig = await getDashboardConfigForEmployee(employee);
  const employeeId = 'currentEngagement' in employee ? parseInt(employee.employeeId) || 0 : employee.id;

  if (!dashboardConfig) {
    throw new Error(
      `🚨 PERFORMANCE DATA ERROR: Cannot get dashboard config for user '${employee.email}'.\n` +
      `User exists but dashboard configuration failed.\n` +
      `Check user role and department data integrity.`
    );
  }

  // If admin, show all employees except themselves
  if (isAdmin || employee.email === 'admin@monumenta.com') {
    const allEmployees = await getAllMonumentaEmployees();
    return allEmployees
      .filter(emp => emp.id !== employeeId) // Exclude the logged user
      .map((emp, index) => convertEmployeeToPerformance(emp, index));
  }

  // If manager, show only direct reports (excludes themselves)
  if (dashboardConfig.isManager && dashboardConfig.directReports.length > 0) {
    return dashboardConfig.directReports
      .filter(emp => emp.id !== employeeId) // Exclude the logged user if somehow in direct reports
      .map((emp, index) => convertEmployeeToPerformance(emp, index));
  }

  // For regular employees, return empty array (they don't manage anyone)
  return [];
};

// Get real employee performance data for current user context (LEGACY - kept for compatibility) - now async
export const getRealEmployeePerformance = async (): Promise<EmployeePerformance[]> => {
  // For People page, we want to show ALL employees
  return await getAllEmployeePerformance();
};